import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useTheme } from 'emotion-theming';
import ResizeObserver from 'react-resize-observer';

import Avatar from '../../../shared/_TBComponents/Avatar/Avatar';
import Table from '../../../shared/components/Table/Table';
import Text from '../../../shared/_TBComponents/Text';

const TableWrapper = styled('div')`
    padding: 0px 20px;
    margin-top: 30px;
  .rdt_TableBody {
    padding: 0px 5px;
  }
`;

const NameCell = styled('div')`
  display: flex;
  align-items: center;

  p {
    margin-left: ${(props) => (props.activeBoards !== 3 ? '15px' : '0px')};
  }
  @media screen and (max-width: 768px) {
    p {
      margin-left: 0px;
    }
  }
`;

const AvatarWrapper = styled('div')`
  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const ChangeCell = styled('div')`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const NoDataComponent = () => null;

const getColumns = (activeBoards, theme, leaderboardForm, windowSize) => {
  const cols = [
    {
      name: 'Rank',
      selector: 'rank',
      width: '65px',
      center: true,
      cell: (row) => (
        <Text styledTextProps={{ fontWeight: 900 }}>
          {row.rank}
        </Text>
      ),
    },
    {
      name: 'Name',
      selector: 'userName',
      style: {
        fontWeight: '700',
      },
      cell: (row) => (
        <NameCell activeBoards={activeBoards}>
          {(activeBoards !== 3) && (
            <AvatarWrapper>
              <Avatar avatar={row.userPic} name={row.userName} />
            </AvatarWrapper>
          )}
          <Text>{row.userName}</Text>
        </NameCell>
      ),
    },
    {
      name: 'Date',
      selector: 'dateSet',
      omit: windowSize.width <= 768,
      center: true,
      style: {
        color: '#444444',
        fontWeight: 400,
      },
    },
    {
      name: 'Result',
      selector: 'displayValue',
      width: activeBoards === 1 ? '150px' : '70px',
      center: true,
      cell: (row) => (
        <Text styledTextProps={{ fontWeight: 600 }}>
          {row.displayValue}
        </Text>
      ),
    },
    {
      name: 'Change',
      selector: 'changeDisplayValue',
      width: activeBoards === 1 ? '150px' : '70px',
      omit: leaderboardForm.compareTo === 0 || activeBoards === 3,
      center: true,
      cell: (row) => (
        <ChangeCell>
          <Text
            styledTextProps={{
              fontWeight: 600,
              fontSize: row.changeDirection === 2 ? 12 : 10,
              color: row.changeDirection === 2 ? theme.colors.green : 'red',
            }}
          >
            {row.changeDirection === 2 && ('▲ ')}
            {row.changeDirection === 1 && ('▼ ')}
          </Text>
          <Text styledTextProps={{ fontWeight: 600 }}>
            {row.changeDisplayValue === null || row.changeDisplayValue === '' || row.changeDisplayValue === 0 ? '-' : row.changeDisplayValue}
          </Text>
        </ChangeCell>
      ),
    },
  ];

  if (activeBoards >= 2) {
    cols.splice(2, 1);
  }

  return cols;
};

const LeaderboardTable = ({
  activeBoards,
  handleLoadMore,
  leaderboardData,
  leaderboardForm,
  tableEnd,
  tableRef,
  setTableHeight,
  setCurrentScroll,
  windowSize,
}) => {
  const theme = useTheme();

  const columns = React.useMemo(
    () => getColumns(activeBoards, theme, leaderboardForm, windowSize),
    [activeBoards, theme, leaderboardForm, windowSize],
  );

  const loadMore = () => {
    handleLoadMore();
  };

  return (
    <TableWrapper ref={tableRef}>
      {/*
        Instead of using a scroll event listener which can be expensive, we're using
        the resize observer api which determines changes to the dimensions and position information
        of an element. By placing it within the TableWrapper we are observing the leaderboard table
        and can use the rect.top data for scroll location.
      */}
      <ResizeObserver
        onResize={(rect) => {
          setTableHeight(rect.height);
        }}
        onPosition={(rect) => {
          // If rect.top from observer api is less than tableEnd from
          // the above variable, then load more data.
          if (rect.top <= tableEnd) {
            loadMore();
            setCurrentScroll(rect.top);
          }
        }}
      />
      <Table
        center
        columns={columns}
        data={leaderboardData}
        highlightOnHover
        noHeader
        noDataComponent={<NoDataComponent />}
      />
    </TableWrapper>
  );
};

LeaderboardTable.propTypes = {
  activeBoards: PropTypes.number.isRequired,
  handleLoadMore: PropTypes.func.isRequired,
  leaderboardData: PropTypes.instanceOf(Array).isRequired,
  leaderboardForm: PropTypes.instanceOf(Object).isRequired,
  tableEnd: PropTypes.number.isRequired,
  tableRef: PropTypes.instanceOf(Object).isRequired,
  setTableHeight: PropTypes.func.isRequired,
  setCurrentScroll: PropTypes.func.isRequired,
  windowSize: PropTypes.instanceOf(Object).isRequired,
};

export default React.memo(LeaderboardTable);
